import { useEffect, useState, useCallback } from "react";
import { api, formatHours } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Car, Wrench, Zap, History, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function HistoryDetails({ data }) {
  const { vehicle, repairs, quick_repairs } = data;
  const allItems = [
    ...repairs.map((r) => ({ ...r, type: "general" })),
    ...quick_repairs.map((r) => ({ ...r, type: "quick" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <Card data-testid="vehicle-history-card">
      <CardHeader>
        <CardTitle className="font-display text-2xl flex items-center gap-3">
          <Car className="w-7 h-7 text-blue-700" />
          <span className="font-mono">{vehicle.license_plate}</span>
        </CardTitle>
        <div className="text-sm text-slate-600 mt-1">
          {vehicle.brand || vehicle.model ? <span>{vehicle.brand} {vehicle.model}</span> : null}
          {vehicle.customer_name && <span className="ml-3">· {vehicle.customer_name}</span>}
          {vehicle.phone && <span className="ml-3">· {vehicle.phone}</span>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm font-medium text-slate-700 mb-3">
          {allItems.length} {allItems.length === 1 ? "registro" : "registros"} en el historial
        </div>
        {allItems.length === 0 ? (
          <div className="text-slate-400 italic">Sin historial para este vehículo.</div>
        ) : (
          <ul className="space-y-3">
            {allItems.map((it) => {
              const isQuick = it.type === "quick";
              const dateLabel = isQuick ? it.date : (it.segments?.[0]?.date || it.created_at?.slice(0, 10));
              return (
                <li key={it.id} className="border border-slate-200 rounded-md p-4" data-testid={`history-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {isQuick ? <Zap className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" /> : <Wrench className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />}
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{it.description}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          <span className={`inline-block px-2 py-0.5 rounded ${isQuick ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                            {isQuick ? "Rápida" : "General"}
                          </span>
                          <span className="ml-2">Cita: {dateLabel}</span>
                          {it.status === "completed" && <span className="ml-2 bg-green-100 text-green-800 px-2 py-0.5 rounded">Completada</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-bold text-slate-900">{formatHours(it.estimated_hours)}</div>
                      {!isQuick && it.segments?.length > 1 && (
                        <div className="text-xs text-amber-600">{it.segments.length} días</div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function Vehicles() {
  const [plate, setPlate] = useState("");
  const [data, setData] = useState(null);
  const [searched, setSearched] = useState(false);
  const [vehicles, setVehicles] = useState([]);

  const loadAll = useCallback(async () => {
    const res = await api.get("/vehicles");
    setVehicles(res.data);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const search = async (e) => {
    e?.preventDefault();
    const p = plate.trim().toUpperCase();
    if (!p) { toast.error("Introduce una matrícula"); return; }
    try {
      const res = await api.get(`/vehicles/${encodeURIComponent(p)}/history`);
      setData(res.data);
      setSearched(true);
      if (res.data.total_count === 0) {
        toast.info(`Sin historial para ${p}`);
      }
    } catch {
      toast.error("Error al buscar");
    }
  };

  const quickSearch = (p) => {
    setPlate(p);
    api.get(`/vehicles/${encodeURIComponent(p)}/history`).then((res) => {
      setData(res.data);
      setSearched(true);
    });
  };

  const backToList = () => {
    setSearched(false);
    setData(null);
    setPlate("");
    loadAll();
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <header>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
          <History className="w-8 h-8 text-blue-700" />
          Historial de Vehículos
        </h1>
        <p className="text-slate-500 mt-1">Busca por matrícula para ver todas las citas de un vehículo.</p>
      </header>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={search} className="flex gap-3">
            <Input
              placeholder="Introduce la matrícula (ej: 1234ABC)"
              value={plate}
              onChange={(e) => setPlate(e.target.value)}
              className="h-12 uppercase font-mono text-lg"
              data-testid="plate-search-input"
            />
            <Button type="submit" className="h-12 bg-blue-700 hover:bg-blue-800 px-6" data-testid="search-plate-btn">
              <Search className="w-4 h-4 mr-2" /> Buscar
            </Button>
          </form>
        </CardContent>
      </Card>

      {searched && data && (
        <>
          <Button
            variant="outline"
            onClick={backToList}
            className="h-12"
            data-testid="back-to-vehicles-list-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver al historial
          </Button>
          <HistoryDetails data={data} />
        </>
      )}

      {!searched && (
        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl">Vehículos atendidos recientemente</CardTitle>
          </CardHeader>
          <CardContent>
            {vehicles.length === 0 ? (
              <div className="text-slate-400 italic" data-testid="vehicles-empty">Aún no hay vehículos registrados. Crea una cita con matrícula.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {vehicles.map((v) => (
                  <li key={v.license_plate} className="py-3 flex items-center justify-between" data-testid={`vehicle-row-${v.license_plate}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <Car className="w-5 h-5 text-slate-400 shrink-0" />
                      <div className="min-w-0">
                        <div className="font-mono font-bold text-slate-900">{v.license_plate}</div>
                        <div className="text-xs text-slate-500">
                          {v.brand} {v.model} {v.customer_name && `· ${v.customer_name}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">{v.count} {v.count === 1 ? "cita" : "citas"}</span>
                      <Button size="sm" variant="outline" onClick={() => quickSearch(v.license_plate)} data-testid={`view-vehicle-${v.license_plate}`}>
                        Ver historial
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
